import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Upload, X } from 'lucide-react';
import { useCreateSubagent, useSubagent, useUpdateSubagent } from '@/features/subagents/hooks';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { useToast } from '@/components/ui/Toast';
import { getApiErrorMessage } from '@/lib/api-client';

interface FormVals {
  name: string;
  description: string;
  tags: string; // CSV no input
}

export default function SubagentFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const nav = useNavigate();
  const toast = useToast();

  const existing = useSubagent(id);
  const create = useCreateSubagent();
  const update = useUpdateSubagent();

  const [file, setFile] = useState<File | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const { register, handleSubmit, setValue } = useForm<FormVals>({
    defaultValues: { name: '', description: '', tags: '' },
  });

  useEffect(() => {
    if (isEdit && existing.data) {
      const s = existing.data;
      setValue('name', s.name);
      setValue('description', s.description);
      setValue('tags', s.tags.map((t) => t.name).join(', '));
    }
  }, [isEdit, existing.data, setValue]);

  // Ao escolher o .md, parseia o frontmatter no cliente e pré-preenche os
  // campos para o usuário revisar/editar antes de salvar. O backend reparseia
  // o arquivo no submit (fonte de verdade), então isto é só conveniência de UX.
  const handleFile = async (f: File | null) => {
    setFile(f);
    if (!f) return;
    try {
      const fm = parseAgentFrontmatter(await f.text());
      if (fm.name) setValue('name', fm.name);
      if (fm.description) setValue('description', fm.description);
      if (fm.tags.length) setValue('tags', fm.tags.join(', '));
    } catch {
      // .md sem frontmatter legível — mantém os campos; o submit ainda valida.
    }
  };

  const onSubmit = async (v: FormVals) => {
    setFormError(null);
    const tagsArr = v.tags
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);

    const name = v.name.trim();
    const description = v.description.trim();

    try {
      if (isEdit) {
        const s = await update.mutateAsync({
          id: id!,
          file: file ?? undefined,
          name: name || undefined,
          description: description || undefined,
          tags: tagsArr,
        });
        toast.success('Subagente atualizado.');
        nav(`/subagentes/${s.id}`);
      } else {
        if (!file) {
          setFormError('Selecione um arquivo .md antes de salvar.');
          return;
        }
        const s = await create.mutateAsync({
          file,
          name: name || undefined,
          description: description || undefined,
          tags: tagsArr,
        });
        toast.success('Subagente criado.');
        nav(`/subagentes/${s.id}`);
      }
    } catch (err) {
      setFormError(getApiErrorMessage(err, 'Falha ao salvar o subagente.'));
    }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <Card>
        <CardHeader>
          <h1 className="text-xl font-semibold">
            {isEdit ? 'Editar subagente' : 'Novo subagente'}
          </h1>
          <p className="text-sm text-text-muted">
            {isEdit
              ? 'Edite os campos e (opcionalmente) substitua o arquivo .md para criar uma nova versão.'
              : 'Arraste o .md do subagente (com frontmatter name/description). Nome e descrição são preenchidos automaticamente ao soltar o arquivo — você pode editar antes de salvar.'}
          </p>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <Dropzone file={file} onFile={handleFile} optional={isEdit} />
            <Input
              label="Nome (opcional)"
              {...register('name')}
              hint="Se vazio, usa o name do frontmatter do .md."
            />
            <Textarea
              label="Descrição curta (opcional)"
              {...register('description')}
              hint="Se vazio, usa o description do frontmatter do .md."
            />
            <Input
              label="Tags"
              {...register('tags')}
              hint="Separe por vírgula. Apenas letras, números e hífens. Ex: backend, revisao"
            />

            {formError && <p className="text-sm text-red-400">{formError}</p>}

            <div className="flex gap-3 justify-end">
              <Button variant="ghost" type="button" onClick={() => nav(-1)}>
                Cancelar
              </Button>
              <Button type="submit" loading={create.isPending || update.isPending}>
                {isEdit ? 'Salvar alterações' : 'Criar subagente'}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}

function Dropzone({
  file,
  onFile,
  optional,
}: {
  file: File | null;
  onFile: (f: File | null) => void;
  optional?: boolean;
}) {
  const [over, setOver] = useState(false);
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) onFile(f);
      }}
      className={
        'border-2 border-dashed rounded-xl p-6 text-center transition ' +
        (over ? 'border-brand-400 bg-brand-500/10' : 'border-border bg-surface-base')
      }
    >
      {file ? (
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Badge tone="brand">.md</Badge>
            <span className="text-sm">{file.name}</span>
            <span className="text-xs text-text-muted">{(file.size / 1024).toFixed(1)} KB</span>
          </div>
          <button
            type="button"
            onClick={() => onFile(null)}
            className="text-text-muted hover:text-text-primary"
            aria-label="Remover"
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        <label className="cursor-pointer flex flex-col items-center gap-2">
          <Upload size={24} className="text-text-muted" />
          <span className="text-sm text-text-secondary">
            Arraste o .md aqui ou clique para selecionar{optional ? ' (opcional)' : ''}
          </span>
          <input
            type="file"
            accept=".md,text/markdown"
            className="hidden"
            onChange={(e) => onFile(e.target.files?.[0] ?? null)}
          />
        </label>
      )}
    </div>
  );
}

/**
 * Parser leniente de frontmatter espelhando o do backend (MarkdownAgentParser):
 * lê linha-a-linha em vez de YAML estrito, porque a `description` do subagente
 * costuma ter `:` no meio. Extrai apenas o que o formulário usa. `tags` não faz
 * parte do formato de subagente do Claude Code, mas é aceito se o .md trouxer.
 */
function parseAgentFrontmatter(raw: string): { name: string; description: string; tags: string[] } {
  const match = raw.match(/^---\r?\n([\s\S]*?)\r?\n---/);
  if (!match) return { name: '', description: '', tags: [] };

  const meta: Record<string, string> = {};
  for (const line of match[1].split(/\r?\n/)) {
    if (!line.trim() || line.trimStart().startsWith('#')) continue;
    const kv = line.match(/^([A-Za-z][A-Za-z0-9_-]*):\s?(.*)$/);
    if (!kv) continue;
    meta[kv[1].toLowerCase()] = stripQuotes(kv[2].trim());
  }

  const tagsRaw = meta.tags ?? '';
  const inner = tagsRaw.startsWith('[') && tagsRaw.endsWith(']') ? tagsRaw.slice(1, -1) : tagsRaw;
  const tags = inner
    .split(',')
    .map((t) => stripQuotes(t.trim()).trim())
    .filter(Boolean);

  return {
    name: (meta.name ?? '').trim(),
    description: (meta.description ?? '').trim(),
    tags,
  };
}

function stripQuotes(v: string): string {
  if (v.length >= 2 && ((v[0] === '"' && v.at(-1) === '"') || (v[0] === "'" && v.at(-1) === "'"))) {
    return v.slice(1, -1);
  }
  return v;
}
